"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  Check,
  ConciergeBell,
  Cpu,
  Factory,
  GraduationCap,
  HardHat,
  Store,
} from "lucide-react";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";

interface IndustriesWeServeProps {
  locale?: Locale;
}

export default function IndustriesWeServe({
  locale = defaultLocale,
}: IndustriesWeServeProps) {
  const prefersReducedMotion = useReducedMotion();
  const translate = (text: string) => t(locale, text);

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.7, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });

  const industries: Array<{
    title: string;
    icon: LucideIcon;
    description: string;
    solutions: string[];
    bentoSpan: string;
  }> = [
    {
      title: "Manufacturing",
      icon: Factory,
      description:
        "Enabling smart manufacturing through IoT integration, automation solutions, and data analytics to optimize production, reduce costs, and improve quality.",
      solutions: [
        "Industrial IoT solutions",
        "Supply chain optimization",
        "Predictive maintenance",
        "Quality control systems",
      ],
      bentoSpan: "lg:col-span-4",
    },
    {
      title: "Wholesale & Retail Trading",
      icon: Store,
      description:
        "Empowering wholesale and retail businesses with integrated solutions that streamline operations, enhance supply chain efficiency, and optimize both B2B and B2C channels for maximum profitability.",
      solutions: [
        "B2B/B2C commerce platforms",
        "Supply chain management",
        "Inventory & warehouse systems",
        "Trade partner integration",
      ],
      bentoSpan: "lg:col-span-2",
    },
    {
      title: "Education",
      icon: GraduationCap,
      description:
        "Building innovative learning platforms and administrative systems to improve educational outcomes, enhance accessibility, and streamline operations.",
      solutions: [
        "Learning management systems",
        "Student information systems",
        "Book inventory management",
        "Bookclub system",
      ],
      bentoSpan: "lg:col-span-2",
    },
    {
      title: "Technology",
      icon: Cpu,
      description:
        "Partnering with technology companies to accelerate product development, implement scalable architectures, and leverage cutting-edge technologies.",
      solutions: [
        "Cloud migration",
        "API development",
        "DevOps implementation",
        "SaaS platform development",
      ],
      bentoSpan: "lg:col-span-2",
    },
    {
      title: "Construction",
      icon: HardHat,
      description:
        "Empowering construction companies to enhance efficiency, improve safety, and drive innovation through technology solutions and data insights.",
      solutions: [
        "Project management software",
        "Safety compliance systems",
        "Plant processing system",
        "Supply chain optimization",
      ],
      bentoSpan: "lg:col-span-2",
    },
    {
      title: "Services",
      icon: ConciergeBell,
      description:
        "Transforming guest experiences and operational efficiency for hotels, restaurants, and service-oriented businesses through innovative digital solutions that enhance customer satisfaction and drive business growth.",
      solutions: [
        "Booking and reservation systems",
        "Customer experience platforms",
        "Property management solutions",
        "Loyalty and rewards programs",
      ],
      bentoSpan: "md:col-span-2 lg:col-span-6",
    },
  ];

  const approaches: Array<{ title: string; description: string }> = [
    {
      title: "Domain Expertise",
      description:
        "We combine deep industry knowledge with technical expertise, understanding the specific challenges, regulations, and opportunities within each sector we serve.",
    },
    {
      title: "Tailored Solutions",
      description:
        "We develop customized solutions that address the unique requirements of each industry, focusing on delivering measurable business outcomes and competitive advantages.",
    },
    {
      title: "Industry Partnerships",
      description:
        "We collaborate with industry leaders and specialized technology providers to deliver comprehensive solutions that leverage best-in-class capabilities.",
    },
  ];

  return (
    <div className="bg-background">
      {/* Unified page header */}
      <section className="bg-background pb-16 pt-24 md:pb-20 md:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.p
            {...reveal(0)}
            className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
          >
            {translate("Industries We Serve")}
          </motion.p>
          <motion.h1
            {...reveal(0.08)}
            className="max-w-4xl font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl lg:text-6xl"
          >
            {translate("Deep expertise across")}{" "}
            <span className="emphasis-underline">
              {translate("your industry")}
            </span>
          </motion.h1>
          <motion.p
            {...reveal(0.16)}
            className="mt-5 max-w-2xl text-lg text-neutral-muted"
          >
            {translate(
              "We bring deep domain knowledge and specialized expertise to a wide range of industries, developing tailored solutions that address the unique challenges and opportunities in each sector.",
            )}
          </motion.p>
        </div>
      </section>
      <div className="h-px w-full bg-border" aria-hidden />

      {/* Industries — bento grid */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("Sector coverage")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {translate("Industry Expertise")}
            </h2>
            <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
              {translate(
                "Six sectors where we have shipped production systems — each with solutions shaped to how the industry actually operates.",
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-6">
            {industries.map(
              (
                { title, description, icon: Icon, solutions, bentoSpan },
                index,
              ) => {
                const isWide =
                  bentoSpan.includes("col-span-4") ||
                  bentoSpan.includes("col-span-6");

                return (
                  <motion.div
                    key={title}
                    initial={
                      prefersReducedMotion ? false : { y: 12 }
                    }
                    whileInView={{ y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{
                      duration: 0.6,
                      delay: (index % 3) * 0.08,
                      ease: [0.21, 0.6, 0.35, 1],
                    }}
                    className={`flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border ${bentoSpan}`}
                  >
                    <div className="mb-6 flex items-start justify-between">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                        <Icon className="h-5 w-5 text-primary" aria-hidden />
                      </div>
                      <span className="font-mono text-sm text-neutral-muted/60">
                        0{index + 1}
                      </span>
                    </div>

                    <div
                      className={
                        isWide
                          ? "flex flex-1 flex-col lg:grid lg:grid-cols-2 lg:items-start lg:gap-12"
                          : "flex flex-1 flex-col"
                      }
                    >
                      <div>
                        <h3 className="mb-3 font-display text-lg font-semibold text-neutral-emphasis">
                          {translate(title)}
                        </h3>
                        <p className="max-w-xl text-sm leading-relaxed text-neutral-muted">
                          {translate(description)}
                        </p>
                      </div>

                      <ul
                        className={
                          isWide
                            ? "mt-6 flex flex-wrap content-center gap-2 lg:mt-0"
                            : "mt-auto flex flex-wrap gap-2 pt-6"
                        }
                      >
                        {solutions.map((solution) => (
                          <li
                            key={solution}
                            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-neutral-muted"
                          >
                            <Check
                              className="h-3 w-3 shrink-0 text-primary"
                              aria-hidden
                            />
                            {translate(solution)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                );
              },
            )}
          </div>
        </div>
      </section>

      {/* Engagement snapshot — the page's dark proof moment */}
      <section className="bg-background pb-24 lg:pb-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.21, 0.6, 0.35, 1] }}
            className="grid overflow-hidden rounded-3xl bg-hero-background lg:grid-cols-2"
          >
            <div className="flex flex-col items-start justify-center p-8 md:p-12 lg:p-14">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary-contrast/60">
                {translate("Engagement snapshot")}
              </p>
              <h2 className="font-display text-3xl font-bold leading-tight text-primary-contrast md:text-4xl">
                {translate("Smart manufacturing on the factory floor")}
              </h2>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-primary-contrast/70">
                {translate(
                  "We connect production lines with IoT sensors, real-time dashboards, and predictive maintenance — starting with focused pilots that show ROI within the first month.",
                )}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <span className="inline-flex items-center rounded-full border border-primary-contrast/15 px-4 py-1.5 text-sm text-primary-contrast/85">
                  {translate("Pilot deployed in 4-8 weeks")}
                </span>
                <span className="inline-flex items-center rounded-full border border-primary-contrast/15 px-4 py-1.5 text-sm text-primary-contrast/85">
                  {translate("Up to 50% less unplanned downtime")}
                </span>
              </div>
              <Link
                href={getLocalizedPathname(
                  "/services/smart-manufacturing/",
                  locale,
                )}
                className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-contrast transition-all duration-200 hover:gap-2.5"
              >
                {translate("Explore smart manufacturing")}
                <span aria-hidden>&rarr;</span>
              </Link>
            </div>
            <div className="relative min-h-[260px] lg:min-h-[360px]">
              <img
                src="/images/services/sm.jpg"
                alt={translate(
                  "Automated production line with industrial robots at a smart manufacturing facility",
                )}
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Industry approach */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("How we work")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {translate("Our Industry Approach")}
            </h2>
            <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
              {translate(
                "Three principles that shape every industry engagement, from first workshop to production rollout.",
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {approaches.map(({ title, description }, index) => (
              <motion.div
                key={title}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.08,
                  ease: [0.21, 0.6, 0.35, 1],
                }}
                className="h-full rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
              >
                <span className="font-mono text-sm text-primary">
                  0{index + 1}
                </span>
                <h3 className="mb-3 mt-3 font-display text-lg font-semibold text-neutral-emphasis">
                  {translate(title)}
                </h3>
                <p className="text-sm leading-relaxed text-neutral-muted">
                  {translate(description)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <div className="h-px w-full bg-border" aria-hidden />

      {/* CTA band */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-neutral-emphasis md:text-5xl">
            {translate("Ready to transform")}{" "}
            <span className="emphasis-underline">
              {translate("your industry?")}
            </span>
          </h2>
          <p className="mt-5 text-lg text-neutral-muted">
            {translate(
              "Let's discuss how our industry-specific expertise can help your organization leverage technology to drive innovation, efficiency, and competitive advantage.",
            )}
          </p>
          <div className="mt-9">
            <Link
              href={getLocalizedPathname("/contact/", locale)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {translate("Meet with Us")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
